"use client";

import { useEffect } from 'react';

/**
 * FacebookSDK Component
 * 
 * This component initializes the Facebook SDK for use with Facebook Feed and Live Streaming features.
 * It's always included in the client layout component but only initialized when needed.
 */
interface FacebookSDKProps {
  shouldInitialize?: boolean;
}

export default function FacebookSDK({ shouldInitialize = false }: FacebookSDKProps) {
  useEffect(() => {
    // Only initialize Facebook SDK when shouldInitialize is true
    if (!shouldInitialize) return;

    // Initialize Facebook SDK
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v16.0' // Use the latest stable version
      });
    };

    // Load the Facebook SDK script only when needed
    (function (d, s, id) {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      const js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode?.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));

    // Cleanup function (optional, as the SDK will remain loaded)
    return () => {
      // No specific cleanup needed for Facebook SDK
    };
  }, [shouldInitialize]); // Re-run effect when shouldInitialize changes

  // This component doesn't render anything visible
  return null;
}

// TypeScript declaration for the global FB object
declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: any;
  }
}